import {priority} from '@priority-software/priority-proto-api/lib';
import {noDefaultMessageHandling, startForm} from '../../../services/form/form';
import {APIActionStatus, Column, Form, formsStore} from '../../../stores/Form';
import {ColumnsRs} from '../types';
import {addNewScreen, handleScreenMessage} from './screen';
import {getColumns, setFormSystemConstants} from './form';
import {addAndApplyFilters, formatFilterValue} from './filters';
import {Sort} from '../../../stores/Form/Sort';
import {BatchRequest} from '../../../services/proto/core/batchRequest';
import {batchKeys} from '../../../services/proto/core/constants';
import {getAdditionalProps} from '../../../services/proto/core/loginProps';
import {CachedFields} from '../../../stores/Form/FormsCache';
import {Options} from '../../../services/proto/core/server';
import {RecordEditState} from '../../../stores/Form/enums';
import {fieldUpdate as fieldUpdateService} from '../../../services/form/form';
import {Field, fieldValue} from '../../../stores/Form/Field';
import {mapValueByColumnType} from '../../../stores/Form/utils';
import {getEntDTitle} from '../../../_shared/GWTBridge/invoke/genPage';
import {MessageHandlers} from '../../../services/proto/core/server';

// these methods let us create a lightweighted instance of a form
// to be partially displayed together with a full grid based instance (old or new api)

export const addNewStandAloneForm = async (
    formName: string,
    queryParams: {[k: string]: string} | null,
    sort?: Sort[],
    top?: number,
    hiddenFields: string[] = null,
    messageHandlers?: MessageHandlers
): Promise<Form | null> => {
    const newForm: Form | null = await startStandAloneForm(
        formName,
        hiddenFields,
        false,
        messageHandlers
    );

    if (newForm) {
        if (top) {
            newForm.maxFetch = top;
        }
        if (queryParams) {
            await fetchDataByQueryParams(newForm, queryParams, sort, top);
        }
        addNewScreen(newForm, null, true);
    }
    return newForm;
};

export const startStandAloneForm = async (
    formName: string,
    hiddenFields: string[] = null,
    suppressErrors: boolean = false,
    messageHandlers?: MessageHandlers
): Promise<Form | null> => {
    await setFormSystemConstants();
    const startRs: priority.netitems.form.StartRs = await startForm(
        formName,
        noDefaultMessageHandling,
        hiddenFields
    );
    const msgHandlers = messageHandlers || {};
    const isFormOk = await handleScreenMessage(startRs, msgHandlers, null, suppressErrors);

    if (isFormOk) {
        const columnsRs: ColumnsRs = await getColumns(startRs.Session, formName);
        const isColumnOk = await handleScreenMessage(columnsRs, null, null, suppressErrors);
        if (isColumnOk) {
            // Create form instance
            const form: Form = new Form(formName, startRs, columnsRs, 0);
            return form;
        }
    }
    return null;
};

export const fetchDataByQueryParams = async (
    newForm: Form,
    queryParams: {[k: string]: string} | null,
    sort?: Sort[],
    top?: number
) => {
    if (queryParams) {
        const filterParams = queryParamsToFilterParams(newForm, queryParams);
        newForm.sort = sort;
        await addAndApplyFilters(newForm, filterParams, top);
    }
};

export const queryParamsToFilterParams = (newForm: Form, queryParams: {[k: string]: string}) => {
    const filterParams = [];
    Object.keys(queryParams).forEach(key => {
        const fieldId = newForm.getColumnByName(key)?.id;
        const value = formatFilterValue(newForm, fieldId, queryParams[key]);
        filterParams.push({fieldId, value});
    });
    return filterParams;
};

// this method is used to start a subform with an additional columns request

export const startSubformWithoutFetch = async (
    form: Form,
    subformName: string,
    suppressErrors: boolean = false
) => {
    const responses = await batchSubformAndColumns(form, subformName);
    const [startRs, columnRs] = responses;

    if (startRs) {
        const isFormOk = await handleScreenMessage(startRs, null, null, suppressErrors);
        if (isFormOk) {
            if (columnRs) {
                const subForm = new Form(subformName, startRs, columnRs, form.level + 1);
                subForm.setParentForm(form);
                formsStore.getScreenBySession(form.session).setActiveLevel(form.level + 1);
            }
        }
    }
};

const batchSubformAndColumns = async (form: Form, subformName: string) => {
    const session = form.session;
    const batchRequest = new BatchRequest(session);

    batchRequest.addTask({
        key: batchKeys.Subform,
        request: new priority.netitems.form.SubformRq({
            Fname: subformName,
            Keys: form.records[form.currentRow].keys,
            Session: form.session,
            Properties: getAdditionalProps()
        })
    });

    batchRequest.addTask({
        key: batchKeys.Columns,
        request: new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        }),
        cacheData: {key: CachedFields.columns, formName: subformName}
    });

    const res = await batchRequest.getData();

    return [res[0]?.Subform, res[1]?.Columns];
};

export const updateCurrentRecordField = async (
    form: Form,
    columnName: string,
    value: fieldValue
) => {
    const column: Column = form.getColumnByName(columnName);
    const field: Field = form.currentRecord.fieldsMap.get(column.id);
    field.setCurrentValue(value);
    return await fieldUpdate(
        form.currentRecord.recordIndex,
        mapValueByColumnType(value, column),
        null,
        form.session
    );
};

export const fieldUpdate = async (
    rowInd: number,
    mappedField: priority.netitems.form.IField,
    options: Partial<Options> = null,
    session: string = null
): Promise<APIActionStatus> => {
    const screen = session ? formsStore.getScreenBySession(session) : formsStore.currentScreen;
    const form = screen.getActiveForm();
    const allowed = await screen.startRecordEdit(form, options);

    if (allowed) {
        let isMultiEnv = false;
        let multiEnvColumn = null;
        form.columnsMap.forEach(col => {
            if (col.MultiEnv) {
                isMultiEnv = true;
                multiEnvColumn = col;
            }
        });
        if (isMultiEnv) {
            const compName = getEntDTitle();
            await updateFieldValue(form, mapValueByColumnType(compName, multiEnvColumn), options);
        }
        const recordUpdateRes = await updateFieldValue(form, mappedField, options);
        // if message has error or warning message, we should let user to fix & update current value on the server
        screen.checkRecordErrorState(recordUpdateRes.Messages);

        return await handleScreenMessage(recordUpdateRes, {}, async res => {
            screen.editMode = RecordEditState.update;
            const updateRes = res || recordUpdateRes;
            form.updateRecordValues(rowInd, updateRes.Record.Fields);
        });
    }
    return APIActionStatus.blocked;
};

export const updateFieldValue = async (
    form: Form,
    Field: priority.netitems.form.IField | null,
    options: Partial<Options>
) => {
    return await fieldUpdateService(form.session, Field, options);
};
